import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  UserCheck,
  ScanLine,
  Activity,
  PlusCircle,
  ArrowUpRight,
  Clock,
  Cpu,
  PackageCheck,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `vor ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `vor ${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h}h`;
  const d = Math.floor(h / 24);
  return `vor ${d}T`;
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState({ customers: [], scans: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/recent"),
        ]);
        setStats(s.data);
        setRecent(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Übersicht</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            Dashboard
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Alle wichtigen Kennzahlen deines 1AI Review Netzwerks.
          </p>
        </div>
        <Link to="/kunden/neu">
          <Button className="rounded-full h-11 px-5 gap-2" data-testid="dashboard-new-customer-btn">
            <PlusCircle className="w-4 h-4" /> Neue Firma hinzufügen
          </Button>
        </Link>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-row-lifecycle">
        <KpiCard
          label="Kunden gesamt"
          value={loading ? "…" : stats?.total_customers ?? 0}
          icon={Users}
          testId="kpi-total-customers"
        />
        <KpiCard
          label="Aktive Kunden"
          value={loading ? "…" : stats?.active_customers ?? 0}
          icon={UserCheck}
          tone="success"
          testId="kpi-active-customers"
        />
        <KpiCard
          label="In Vorbereitung"
          value={loading ? "…" : stats?.preparing_customers ?? 0}
          icon={Cpu}
          tone="warning"
          testId="kpi-preparing-customers"
        />
        <KpiCard
          label="Ausgeliefert"
          value={loading ? "…" : stats?.delivered_customers ?? 0}
          icon={PackageCheck}
          tone="success"
          testId="kpi-delivered-customers"
        />
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-row-secondary">
        <KpiCard
          label="Verkaufsbereit"
          value={loading ? "…" : stats?.ready_customers ?? 0}
          icon={Sparkles}
          tone="primary"
          testId="kpi-ready-customers"
        />
        <KpiCard
          label="Neu (unvorbereitet)"
          value={loading ? "…" : stats?.new_customers ?? 0}
          icon={PlusCircle}
          tone="muted"
          testId="kpi-new-customers"
        />
        <KpiCard
          label="Scans heute"
          value={loading ? "…" : stats?.scans_today ?? 0}
          icon={ScanLine}
          tone="warning"
          testId="kpi-scans-today"
        />
        <KpiCard
          label="Scans gesamt"
          value={loading ? "…" : stats?.total_scans ?? 0}
          icon={Activity}
          tone="muted"
          testId="kpi-scans-total"
        />
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface p-5" data-testid="recent-customers-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold">Neueste Kunden</h3>
              <p className="text-xs text-muted-foreground">Zuletzt hinzugefügte Firmen</p>
            </div>
            <Link to="/kunden" className="text-xs text-primary flex items-center gap-1 hover:underline">
              Alle ansehen <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          {recent.customers?.length ? (
            <ul className="divide-y divide-white/[0.05]">
              {recent.customers.map((c) => (
                <li key={c.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={`/kunden/${c.id}`}
                      className="text-sm font-medium hover:text-primary truncate block"
                    >
                      {c.business_name}
                    </Link>
                    <div className="text-xs text-muted-foreground truncate">{c.address}</div>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      c.status === "active"
                        ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
                        : "border-white/10 text-muted-foreground"
                    }
                  >
                    {c.status === "active" ? "Aktiv" : "Inaktiv"}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState label="Noch keine Kunden angelegt." />
          )}
        </div>

        <div className="card-surface p-5" data-testid="recent-scans-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold">Letzte Scans</h3>
              <p className="text-xs text-muted-foreground">Ereignisse in Echtzeit</p>
            </div>
          </div>
          {recent.scans?.length ? (
            <ul className="divide-y divide-white/[0.05]">
              {recent.scans.map((s) => (
                <li key={s.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{s.business_name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {s.device_type || "unbekannt"} · {s.browser || ""} · {s.operating_system || ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                    <Clock className="w-3 h-3" />
                    {timeAgo(s.timestamp)}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState label="Noch keine Scans erfasst." />
          )}
        </div>
      </section>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="py-10 text-center text-sm text-muted-foreground border border-dashed border-white/[0.08] rounded-lg">
      {label}
    </div>
  );
}
